import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterFrame, FilterSelection, GroupWrapper } from '../Filters.style';
import { RangeFilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import {
  AllYearsWrapper,
  CTAWrapper,
  ModelYearFilterWrapper,
  ShowAllButton,
} from './ModelYearSelector.style';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { AvailableFiltersCount } from '../../../types/oneGraphTypes';
import CheckboxSelector from '../../CheckboxComponent';

interface ModelYearSelectorTypes {
  filterType: string;
  confirmedFilters: FilterTypes;
  filterOptions: AvailableFiltersCount[];
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const ModelYearSelector: React.FC<ModelYearSelectorTypes> = ({
  filterType,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  filterOptions,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const [showAllYears, setShowAllYears] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const { vehicleType } = confirmedFilters;

  const isNewTypeSelected = vehicleType === 'new';

  const yearsToRender = useMemo(() => {
    const visibleYears = isNewTypeSelected ? filterOptions.slice(0, 3) : filterOptions.slice(0, 5);
    const hiddenYears = filterOptions.slice(visibleYears.length);
    return showAllYears ? [...visibleYears, ...hiddenYears] : visibleYears;
  }, [filterOptions, isNewTypeSelected, showAllYears]);

  const numberOfRowsInGrid = useMemo(() => {
    if (isNewTypeSelected) return 2;
    return showAllYears ? 9 : 5;
  }, [isNewTypeSelected, showAllYears]);

  const selectedYears = confirmedFilters.modelYear;

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAllYears]);

  return (
    <Filter showFilter={showFilter}>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedYears}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="model-year-filter">
              <FilterFrame style={filtersAccordianAnimation}>
                <AllYearsWrapper
                  expanded={showAllYears}
                  ref={contentRef}
                  contentHeight={`${contentHeight}px` || '116'}
                >
                  <ModelYearFilterWrapper numberOfRowsInGrid={numberOfRowsInGrid}>
                    {yearsToRender.map((filterOption) => {
                      const { value, disabled, count } = filterOption;
                      return (
                        <CheckboxSelector
                          filterType={filterType}
                          inputName={`${filterType}-${value}`}
                          key={value}
                          onChange={() => handleAddFilter(filterType, value)}
                          value={value}
                          checked={selectedYears.includes(value)}
                          count={count}
                          disabled={disabled}
                        />
                      );
                    })}
                  </ModelYearFilterWrapper>
                </AllYearsWrapper>
                {!isNewTypeSelected && (
                  <CTAWrapper expanded={showAllYears}>
                    <ShowAllButton
                      onClick={() => setShowAllYears(!showAllYears)}
                      data-testid="show-all-filters"
                    >
                      {showAllYears
                        ? t(`vlp.filters.modelYear.showFewer`)
                        : t(`vlp.filters.modelYear.showAll`)}
                    </ShowAllButton>
                  </CTAWrapper>
                )}
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default ModelYearSelector;
